import { useState, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import { Upload, X, Image as ImageIcon } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'react-hot-toast';

export default function LogoUpload() {
  const { user } = useAuth();
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const recipeFileInputRef = useRef<HTMLInputElement>(null);
  const adminUserId = import.meta.env.VITE_ADMIN_USER_ID;

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>, type: 'logo' | 'recipe') => {
    try {
      if (!user || user.id !== adminUserId) {
        toast.error('Only admin can update assets');
        return;
      }

      const file = e.target.files?.[0];
      if (!file) return;

      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please upload an image file');
        return;
      }

      // Validate file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Image size should be less than 2MB');
        return;
      }

      setUploading(true);

      // Upload to storage
      const fileExt = file.name.split('.').pop();
      const fileName = type === 'logo' ? `logo.${fileExt}` : `recipe-placeholder.${fileExt}`;
      const filePath = fileName;

      let { error: uploadError } = await supabase.storage
        .from('app-assets')
        .upload(filePath, file, { 
          upsert: true,
          contentType: file.type
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('app-assets')
        .getPublicUrl(filePath);

      // Update app settings
      const { error: updateError } = await supabase
        .from('app_settings')
        .update({ 
          [type === 'logo' ? 'logo_url' : 'recipe_placeholder_url']: publicUrl 
        })
        .eq('id', 1);

      if (updateError) throw updateError;

      toast.success(`${type === 'logo' ? 'Logo' : 'Recipe placeholder'} updated successfully`);
      window.location.reload(); // Refresh to show new assets
    } catch (error: any) {
      console.error('Error uploading:', error);
      toast.error(error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveAsset = async (type: 'logo' | 'recipe') => {
    try {
      if (!user || user.id !== adminUserId) {
        toast.error('Only admin can update assets');
        return;
      }

      setUploading(true);

      // Update app settings to remove asset
      const { error: updateError } = await supabase
        .from('app_settings')
        .update({ 
          [type === 'logo' ? 'logo_url' : 'recipe_placeholder_url']: null 
        })
        .eq('id', 1);

      if (updateError) throw updateError;

      // Delete from storage
      const { error: deleteError } = await supabase.storage
        .from('app-assets')
        .remove([
          type === 'logo' ? 'logo.png' : 'recipe-placeholder.png',
          type === 'logo' ? 'logo.jpg' : 'recipe-placeholder.jpg',
          type === 'logo' ? 'logo.jpeg' : 'recipe-placeholder.jpeg',
          type === 'logo' ? 'logo.gif' : 'recipe-placeholder.gif',
          type === 'logo' ? 'logo.svg' : 'recipe-placeholder.svg'
        ]);

      if (deleteError && !deleteError.message.includes('Object not found')) {
        throw deleteError;
      }

      toast.success(`${type === 'logo' ? 'Logo' : 'Recipe placeholder'} removed successfully`);
      window.location.reload();
    } catch (error: any) {
      console.error('Error removing asset:', error);
      toast.error(error.message);
    } finally {
      setUploading(false);
    }
  };

  // Only render if user is admin
  if (!user || user.id !== adminUserId) return null;

  return (
    <div className="fixed bottom-4 right-4 flex gap-2">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={(e) => handleUpload(e, 'logo')}
        className="hidden"
        id="logo-upload"
      />
      <input
        ref={recipeFileInputRef}
        type="file"
        accept="image/*"
        onChange={(e) => handleUpload(e, 'recipe')}
        className="hidden"
        id="recipe-placeholder-upload"
      />
      
      <div className="flex flex-col gap-2">
        <div className="flex gap-2">
          <label
            htmlFor="logo-upload"
            className={`cursor-pointer bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 flex items-center gap-2 ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <Upload className="h-4 w-4" />
            {uploading ? 'Uploading...' : 'Upload Logo'}
          </label>
          <Button
            variant="outline"
            size="icon"
            onClick={() => handleRemoveAsset('logo')}
            disabled={uploading}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex gap-2">
          <label
            htmlFor="recipe-placeholder-upload"
            className={`cursor-pointer bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 flex items-center gap-2 ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <ImageIcon className="h-4 w-4" />
            {uploading ? 'Uploading...' : 'Upload Recipe Placeholder'}
          </label>
          <Button
            variant="outline"
            size="icon"
            onClick={() => handleRemoveAsset('recipe')}
            disabled={uploading}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}